import type { DataHealthResponse, DataHealthSnapshot } from "./types";

export const SNAPSHOT_STALE_SEC = 600;

type DataHealthTone = "green" | "amber" | "blue" | "red";

const TOP_STATUS_LABEL_KO: Record<string, string> = {
  healthy: "수집 정상",
  degraded: "수집 이상",
  stale: "스냅샷 오래됨",
  unknown: "수집 불명",
};

function formatIsoShort(iso?: string | null) {
  const ms = Date.parse(String(iso ?? ""));
  if (!Number.isFinite(ms)) return "—";
  return new Date(ms).toISOString().slice(0, 16).replace("T", " ");
}

function formatBps(v?: number | null) {
  if (v == null || !Number.isFinite(v)) return "—";
  const rounded = Math.round(v * 10) / 10;
  return `${rounded > 0 ? "+" : ""}${rounded}bps`;
}

export function resolveTopStatus(snapshot?: DataHealthSnapshot, observedAt?: string) {
  if (!snapshot) return "unknown";

  const generatedMs = Date.parse(String(snapshot.generatedAt ?? ""));
  const observedMs = Date.parse(String(observedAt ?? ""));
  if (Number.isFinite(generatedMs) && Number.isFinite(observedMs)) {
    const ageSec = Math.max(0, (observedMs - generatedMs) / 1000);
    if (ageSec > SNAPSHOT_STALE_SEC) return "stale";
  }

  if (snapshot.effectiveTopStatus) return snapshot.effectiveTopStatus;
  return snapshot.topStatus ?? "unknown";
}

function topStatusTone(status: string): DataHealthTone {
  if (status === "healthy") return "green";
  if (status === "stale") return "red";
  if (status === "degraded") return "amber";
  return "amber";
}

function offenseCounts(snapshot?: DataHealthSnapshot) {
  const offense = snapshot?.offense;
  if (!offense) return "측정 대기";
  if (offense.measurementState === "success") {
    const qualified = offense.totalQualifiedCohorts ?? 0;
    return `자격 코호트 ${qualified} · ${offense.barMet ? "바 충족" : "바 미달"}`;
  }
  if (offense.lastSuccessAt) {
    const best = offense.bestHorizon;
    const detail = best ? `${best.horizon} ${formatBps(best.evLowerBps)}` : "이전값";
    return `측정 ${offense.measurementState} · 마지막 ${formatIsoShort(offense.lastSuccessAt)} (${detail})`;
  }
  return `측정 ${offense.measurementState ?? "unknown"}`;
}

function offenseReview(snapshot?: DataHealthSnapshot) {
  const offense = snapshot?.offense;
  const streams = snapshot?.streamStatus ?? "—";
  const runtime = snapshot?.runtime;
  const ingest = snapshot?.alertIngest;
  const ingestLabel = ingest?.available
    ? ingest.failingCount
      ? `ingest 실패 ${ingest.failingCount}`
      : "ingest ok"
    : "ingest unavailable";
  const offenseLabel =
    offense?.measurementState === "success"
      ? offense.barMet
        ? "엣지 바 충족"
        : "엣지 바 미달 (관측 진행)"
      : offense?.lastSuccessAt
        ? "엣지 측정 불가 · 마지막 정상값 유지"
        : "엣지 측정 대기";
  return `스트림 ${streams} · ws ${runtime?.wsUp ? "up" : "DOWN"} · runtime ${runtime?.runtimeInstances ?? "?"} · ${ingestLabel} · ${offenseLabel}`;
}

export function summarizeDataHealthOperatorLine(
  response?: DataHealthResponse,
  options?: { failed?: boolean },
): {
  tone: DataHealthTone;
  title: string;
  counts: string;
  review: string;
} {
  if (!response || options?.failed || !response.available || !response.snapshot) {
    return {
      tone: "amber",
      title: "데이터 수집 & 엣지",
      counts: "스냅샷 없음",
      review:
        response?.reason === "snapshot_missing"
          ? "data-health-snapshot 미생성 — research:data-health 실행"
          : "관측 스냅샷 로드 실패",
    };
  }

  const topStatus = resolveTopStatus(response.snapshot, response.observedAt);
  const tone = topStatusTone(topStatus);
  return {
    tone,
    title: "데이터 수집 & 엣지",
    counts: `${TOP_STATUS_LABEL_KO[topStatus] ?? topStatus} · ${offenseCounts(response.snapshot)}`,
    review: offenseReview(response.snapshot),
  };
}

export function buildTrajectoryRows(response?: DataHealthResponse) {
  const records = response?.trajectory ?? [];
  return records.slice(-12).map((point) => ({
    id: point.observedAt ?? crypto.randomUUID(),
    observedAt: formatIsoShort(point.observedAt),
    streamStatus: point.streamStatus ?? "—",
    topStatus: point.topStatus ?? "—",
    barMet: point.barMet == null ? "—" : point.barMet ? "Y" : "N",
    horizons: (point.byHorizonLB ?? [])
      .map((h) => `${h.h}:${formatBps(h.lb)} n=${h.n ?? "—"}`)
      .join(" · "),
  }));
}

export function buildHorizonRows(snapshot?: DataHealthSnapshot) {
  return (snapshot?.offense?.byHorizon ?? []).map((row) => ({
    horizon: row.horizon,
    n: row.n == null ? "—" : row.n.toLocaleString("ko-KR"),
    evMeanBps: formatBps(row.evMeanBps),
    evLowerBps: formatBps(row.evLowerBps),
    qualifiedCohorts: row.qualifiedCohorts ?? 0,
  }));
}

export function buildStreamRows(snapshot?: DataHealthSnapshot) {
  return (snapshot?.streams ?? []).map((stream) => ({
    label: stream.label ?? stream.key ?? "stream",
    status: stream.status ?? "unknown",
    ageLabel:
      stream.ageSeconds == null || !Number.isFinite(stream.ageSeconds)
        ? "—"
        : stream.ageSeconds < 90
          ? `${Math.round(stream.ageSeconds)}초 전`
          : stream.ageSeconds < 5400
            ? `${Math.round(stream.ageSeconds / 60)}분 전`
            : `${Math.round(stream.ageSeconds / 3600)}시간 전`,
    rowsToday: stream.rowsToday == null ? "—" : stream.rowsToday.toLocaleString("ko-KR"),
    note: stream.note ?? "",
  }));
}
